/**
 * Product Bundle Script
 * Xử lý logic chọn sản phẩm bundle và add to cart
 * Version: Non-intrusive - Không can thiệp vào Giftbox flow
 */

class ProductBundle {
  constructor() {
    this.wrapper = document.querySelector('.product-bundle-wrapper');
    if (!this.wrapper) return;

    this.maxSelect = parseInt(this.wrapper.dataset.maxSelect) || 2;
    this.checkboxes = this.wrapper.querySelectorAll('.product-bundle__checkbox');
    this.message = this.wrapper.querySelector('.product-bundle__message');
    this.messageText = this.wrapper.querySelector('.product-bundle__message-text');
    this.addToCartButton = document.querySelector('[name="add"]');
    this.productForm = document.querySelector('form[action*="/cart/add"]');
    
    this.isAddingBundle = false; // Flag để track bundle đang được add

    this.init();
  }

  init() {
    // Xử lý sự kiện checkbox change
    this.checkboxes.forEach(checkbox => {
      checkbox.addEventListener('change', this.handleCheckboxChange.bind(this));
      
      // Xử lý click vào item để toggle checkbox
      const item = checkbox.closest('.product-bundle__item');
      if (item) {
        item.addEventListener('click', (e) => {
          // Không toggle nếu click trực tiếp vào checkbox
          if (e.target.type !== 'checkbox') {
            if (!checkbox.disabled) {
              checkbox.checked = !checkbox.checked;
              this.handleCheckboxChange({ target: checkbox });
            }
          }
        });
      }
    });

    // Intercept form submit ở CAPTURE PHASE (chạy trước tất cả)
    if (this.productForm) {
      this.productForm.addEventListener('submit', this.handleFormSubmit.bind(this), true);
    }
  }

  /**
   * Xử lý khi checkbox thay đổi
   */
  handleCheckboxChange(e) {
    const checkbox = e.target;
    const item = checkbox.closest('.product-bundle__item');
    const selectedCount = this.getSelectedCount();

    // Toggle class selected
    if (checkbox.checked) {
      item.classList.add('selected');
    } else {
      item.classList.remove('selected');
    }

    // Kiểm tra giới hạn số lượng
    if (selectedCount > this.maxSelect) {
      checkbox.checked = false;
      item.classList.remove('selected');
      this.showMessage(`Bạn chỉ có thể chọn tối đa ${this.maxSelect} sản phẩm`);
      return;
    }

    // Disable/Enable các checkbox còn lại khi đạt max
    if (selectedCount >= this.maxSelect) {
      this.disableUnselectedCheckboxes();
      this.showMessage(`Đã chọn ${selectedCount}/${this.maxSelect} sản phẩm`);
    }
    // else if(selectedCount >= 1){
    //   this.showMessage(`Đã chọn ${selectedCount}/${this.maxSelect} sản phẩm`);
    // }
     else {
      this.enableAllCheckboxes();
      this.hideMessage();
    }
  }

  /**
   * Đếm số checkbox đang được chọn
   */
  getSelectedCount() {
    return Array.from(this.checkboxes).filter(cb => cb.checked && !cb.disabled).length;
  }

  /**
   * Disable các checkbox chưa được chọn
   */
  disableUnselectedCheckboxes() {
    this.checkboxes.forEach(cb => {
      if (!cb.checked && !cb.disabled) {
        cb.disabled = true;
        cb.dataset.tempDisabled = 'true';
      }
    });
  }

  /**
   * Enable lại các checkbox (trừ sold out)
   */
  enableAllCheckboxes() {
    this.checkboxes.forEach(cb => {
      if (cb.dataset.tempDisabled === 'true') {
        cb.disabled = false;
        delete cb.dataset.tempDisabled;
      }
    });
  }

  /**
   * Hiển thị thông báo
   */
  showMessage(text) {
    this.messageText.textContent = text;
    this.message.style.display = 'block';
  }

  /**
   * Ẩn thông báo
   */
  hideMessage() {
    this.message.style.display = 'none';
  }

  /**
   * Lấy danh sách variant ID đã chọn
   */
  getSelectedVariants() {
    return Array.from(this.checkboxes)
      .filter(cb => cb.checked)
      .map(cb => ({
        id: cb.dataset.variantId,
        title: cb.dataset.productTitle
      }));
  }

  /**
   * Xử lý submit form
   * Chạy ở CAPTURE PHASE (trước Giftbox)
   */
  handleFormSubmit(e) {
    const selectedBundleProducts = this.getSelectedVariants();

    // Nếu KHÔNG có bundle nào được chọn → Để Giftbox xử lý bình thường
    if (selectedBundleProducts.length === 0) {
      return; // Không làm gì cả
    }

    // Nếu CÓ bundle được chọn → Chặn submit và xử lý
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation(); // Chặn tất cả listeners khác

    // Nếu đang process → Tránh double submit
    if (this.isAddingBundle) {
      console.log('Bundle is being added, please wait...');
      return;
    }

    this.isAddingBundle = true;
    this.setButtonLoading(true);

    // Add bundle products trước
    this.addBundleProducts(selectedBundleProducts)
      .then(() => {
        console.log('✓ Bundle products added, now submitting form for Giftbox...');
        
        // Reset flag
        this.isAddingBundle = false;
        
        // Sau khi add bundle xong → Submit form lại để Giftbox xử lý
        this.submitFormForGiftbox();
      })
      .catch((error) => {
        console.error('Error adding bundle products:', error);
        alert('Có lỗi xảy ra khi thêm bundle products. Vui lòng thử lại.');
        this.isAddingBundle = false;
        this.setButtonLoading(false);
      });
  }

  /**
   * Add bundle products vào cart
   */
  async addBundleProducts(bundleProducts) {
    const items = bundleProducts.map(product => ({
      id: product.id,
      quantity: 1
    }));

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Failed to add bundle products: ${errorData.description || 'Unknown error'}`);
    }

    const data = await response.json();
    console.log('✓ Bundle products added to cart:', data);
    
    // Reset bundle selection sau khi add thành công
    this.resetBundleSelection();
    
    return data;
  }

  /**
   * Submit form lại để Giftbox xử lý
   */
  submitFormForGiftbox() {
    // Tạo flag để bundle script không intercept lần nữa
    this.productForm.dataset.bundleProcessed = 'true';

    // Enable button trước khi submit
    this.setButtonLoading(false);

    // Delay nhỏ để đảm bảo flag được set
    setTimeout(() => {
      // Trigger submit event bình thường (bubble phase)
      const submitEvent = new Event('submit', {
        bubbles: true,
        cancelable: true
      });
      
      this.productForm.dispatchEvent(submitEvent);

      // Nếu event không bị prevent → Submit form thật
      if (!submitEvent.defaultPrevented) {
        this.productForm.submit();
      }

      // Reset flag sau khi submit
      setTimeout(() => {
        delete this.productForm.dataset.bundleProcessed;
      }, 1000);
    }, 100);
  }

  /**
   * Check xem bundle đã được process chưa
   */
  isBundleProcessed() {
    return this.productForm && this.productForm.dataset.bundleProcessed === 'true';
  }

  /**
   * Set trạng thái loading cho button
   */
  setButtonLoading(isLoading) {
    const button = this.addToCartButton || this.productForm.querySelector('[type="submit"]');
    if (button) {
      if (isLoading) {
        button.disabled = true;
        button.dataset.originalText = button.textContent;
        button.textContent = 'Adding bundle...';
      } else {
        button.disabled = false;
        if (button.dataset.originalText) {
          button.textContent = button.dataset.originalText;
        }
      }
    }
  }

  /**
   * Reset bundle selection
   */
  resetBundleSelection() {
    this.checkboxes.forEach(cb => {
      cb.checked = false;
      const item = cb.closest('.product-bundle__item');
      if (item) {
        item.classList.remove('selected');
      }
    });
    this.enableAllCheckboxes();
    this.hideMessage();
  }
}

// Initialize khi DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ProductBundle();
  });
} else {
  new ProductBundle();
}
